

import { useState, useEffect, useRef, useCallback } from "react"
import NavigationButtons from "../../Components/CityFeatures/NavigationButtons"
import Overview from "../../Components/CityFeatures/Sections/Overview"
import Infrastructure from "../../Components/CityFeatures/Sections/Infrastructure"
import InstituitionalCapacity from "../../Components/CityFeatures/Sections/InstituitionalCapacity"
import Sanitation from "../../Components/CityFeatures/Sections/Sanitation"
import Indicators from "../../Components/CityFeatures/Sections/Indicators"

const CityProfile = () => {
  const [selectedButton, setSelectedButton] = useState("Overview")
  const [firstCitySelection, setFirstCitySelection] = useState({})
  const [secondCitySelection, setSecondCitySelection] = useState({})
  const [isScrolling, setIsScrolling] = useState(false)

  const sections = {
    Overview: useRef(null),
    Infrastructure: useRef(null),
    InstituitionalCapacity: useRef(null),
    Sanitation: useRef(null),
    Indicators: useRef(null),
  }

  const sectionNames = Object.keys(sections)

  const handleButtonClick = useCallback((button) => {
    setIsScrolling(true)
    setSelectedButton(button)

    const section = document.getElementById(button)
    section?.scrollIntoView({ behavior: "smooth", block: "start" })

    requestAnimationFrame(() => {
      window.scrollBy({ top: -80, behavior: "smooth" })
    })

    setTimeout(() => {
      setIsScrolling(false)
    }, 1000)
  }, [])

  // Simplified and more reliable intersection observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-80px 0px -30% 0px", // Balanced for both short and long sections
      threshold: 0.1, // Simple single threshold
    }

    const observerCallback = (entries) => {
      if (isScrolling) return

      // Find all currently intersecting sections
      const intersectingSections = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => ({
          id: entry.target.id,
          ratio: entry.intersectionRatio,
          top: entry.boundingClientRect.top,
        }))

      
      if (intersectingSections.length > 0) {
        // If multiple sections are intersecting, choose the one closest to the top
        const activeSection = intersectingSections.reduce((closest, current) => {
          // Prefer the section that's closer to the top of viewport (but still visible)
          return Math.abs(current.top) < Math.abs(closest.top) ? current : closest
        })

        if (activeSection.id !== selectedButton) {
          setSelectedButton(activeSection.id)
        }
      } else {
        // Fallback: find section closest to viewport center (for very short sections)
        const viewportCenter = window.innerHeight / 2
        let closestSection = null
        let closestDistance = Number.POSITIVE_INFINITY

        sectionNames.forEach((sectionName) => {
          const element = sections[sectionName].current
          if (element) {
            const rect = element.getBoundingClientRect()
            const sectionCenter = rect.top + rect.height / 2
            const distance = Math.abs(viewportCenter - sectionCenter)

            // Only consider sections that are at least partially visible
            if (rect.bottom > 0 && rect.top < window.innerHeight && distance < closestDistance) {
              closestDistance = distance
              closestSection = sectionName
            }
          }
        })

        if (closestSection && closestSection !== selectedButton) {
          setSelectedButton(closestSection)
        }
      }
    }


    const observer = new IntersectionObserver(observerCallback, observerOptions)

    // Observe all section elements
    sectionNames.forEach((sectionName) => {
      const sectionElement = sections[sectionName].current
      if (sectionElement) {
        observer.observe(sectionElement)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [firstCitySelection, secondCitySelection, isScrolling, selectedButton])

  return (
    <div className="border border-gray-300 p-2 lg:p-5">
      <div className="flex flex-wrap mb-7">
        <div className="lg:flex justify-between w-full mb-3">
          <h1 className="text-[#2F4B7C] text-xl font-semibold text-start basis-3/4">Please choose the City and Year</h1>
        </div>

        <div
          className="w-full sticky top-0 flex bg-gradient-to-br from-[#ffffff89] to-[#e7e7e70c] via-[#e7e7e781]"
          style={{ zIndex: 2 }}
        >
          <NavigationButtons
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            handleButtonClick={handleButtonClick}
            setCitySelection={{
              first: setFirstCitySelection,
              second: setSecondCitySelection,
            }}
          />
        </div>

        {/* Sections */}
        {secondCitySelection?.isComparison ? (
          (firstCitySelection?.city && firstCitySelection?.dataSource?.length) ||
          (secondCitySelection?.city && secondCitySelection?.dataSource?.length) ? (
            <div className="space-y-24 w-full">
              {[
                { Component: Overview, name: "Overview" },
                { Component: Infrastructure, name: "Infrastructure" },
                { Component: InstituitionalCapacity, name: "InstituitionalCapacity" },
                 { Component: Sanitation, name: "Sanitation" },
                { Component: Indicators, name: "Indicators" },
              ].map(({ Component, name }, index) => (
               
                  <div key={index} ref={sections[name]} id={name}>
                  <Component isComparison={true} firstCity={firstCitySelection} secondCity={secondCitySelection} />
                </div>
               
                
              ))}
            </div>
          ) : (
            <div className="py-20 text-lg font-semibold text-center flex justify-center w-full">
              {/* <p className="border-2 p-2 px-8">{getSelectionValidation()}</p> */}
            </div>
          )
        ) : firstCitySelection?.city && firstCitySelection?.dataSource?.length ? (
          <div className="space-y-24 w-full">
            {[
              { Component: Overview, name: "Overview" },
              { Component: Infrastructure, name: "Infrastructure" },
              { Component: InstituitionalCapacity, name: "InstituitionalCapacity" },
              { Component: Sanitation, name: "Sanitation" },
             { Component: Indicators, name: "Indicators" },
            ].map(({ Component, name }, index) => (
           <div key={index} ref={sections[name]} id={name}>
                <Component isComparison={false} firstCity={firstCitySelection} />
              </div>
           
            ))}
          </div>
        ) : (
          <div className="py-20 text-lg font-semibold text-center flex justify-center w-full">
            {/* <p className="border-2 p-2 px-8">{getSelectionValidation()}</p> */}
          </div>
        )}
      </div>
    </div>
  )
}

export default CityProfile
